import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import { rgba } from 'polished';

import {
    Sizes,
    Colours,
    Typography,
    ModalContainer,
} from '@ratehub/base-ui';


function CardCalculationModal({
    children,
    isOpen = true,
    onClose,
    ...otherProps
}) {
    return (
        <ModalContainer
            isOpen={isOpen}
            onClose={onClose}
            showBranding={false}
            contentMaxWidth="32.5em"        /* 520px */
            {...otherProps}
        >
            <MyInnerModalContainer className="rh-box-sizing-border-box rh-bg-coconut">
                {children}
            </MyInnerModalContainer>
        </ModalContainer>
    );
}

CardCalculationModal.propTypes = {
    children: PropTypes.node.isRequired,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
};


const BORDER_RADIUS = '8px';

const MyInnerModalContainer = styled.div`
    margin: ${Sizes.SPACING.QUARTER};
`;

CardCalculationModal.DefinitionListWrapper = styled.div`
    border: 1px solid ${Colours.YUZU};
    border-radius: ${BORDER_RADIUS};

    box-shadow: 1px 0 2px 2px ${Colours.STONE_LIGHTEST},
        0 1px 2px 2px ${Colours.STONE_LIGHTEST},
        -1px 0 2px 2px ${Colours.STONE_LIGHTEST},
        0 -1px 2px 2px ${Colours.STONE_LIGHTEST};

    &.space-above {
        margin-top: ${Sizes.SPACING.HALF};
    }
`;

// within the ContentGrid, each child must be either a dt, a dd, or a div
CardCalculationModal.ContentGrid = styled.dl`
    font-size: ${Sizes.FONTS.XS};
    margin: 0;

    display: grid;
    grid-template-columns: 1fr max-content;
    &.second-column-fixed {
        // required when showing .ContentGrid more than once so their column widths are the same
        grid-template-columns: 1fr 34%;
    }
    grid-template-rows: auto;

    line-height: 1.2;

    small {
        font-size: ${Sizes.FONTS['2XS']};
        font-weight: ${Typography.WEIGHTS.REGULAR};
        line-height: 1.5;
    }
`;

CardCalculationModal.Description = styled.dt`
    display: inline-flex;
    justify-content: space-between;

    padding: ${Sizes.SPACING.HALF} ${Sizes.SPACING.HALF} ${Sizes.SPACING.HALF} ${Sizes.SPACING.ONE_AND_A_HALF};

    &.isBottomSummary {
        padding-top: ${Sizes.SPACING.ONE};
        padding-bottom: ${Sizes.SPACING.ONE};
        border-radius: 0 0 0 ${BORDER_RADIUS};
    }

    line-height: 1.2;

    &.bringAttention {
        font-weight: ${Typography.WEIGHTS.MEDIUM};
    }

    // wrapper element for +/- preceding calculation components
    .operator {
        display: inline-flex;
        justify-content: center;
        padding: 0 2px;
        width: 0.7rem;
        font-weight: ${Typography.WEIGHTS.MEDIUM};
        color: ${Colours.BLACKBERRY};
        background-color: ${rgba(Colours.YUZU, 0.3)};
        border-radius: 25%;
    }
`;

CardCalculationModal.Value = styled.dd`
    padding: ${Sizes.SPACING.HALF} ${Sizes.SPACING.ONE_AND_A_HALF} ${Sizes.SPACING.HALF} ${Sizes.SPACING.HALF};

    &.isBottomSummary {
        padding-top: ${Sizes.SPACING.ONE};
        padding-bottom: ${Sizes.SPACING.ONE};
        border-radius: 0 0 ${BORDER_RADIUS} 0;
    }

    margin-left: 0;

    & > output {
        &.bringAttention {
            font-weight: ${Typography.WEIGHTS.MEDIUM};
        }

        &.fee {
            color: ${Colours.STRAWBERRY};
        }

        &.waivedFee {
            color: ${Colours.BLUEBERRY};
        }

        &.perk {
            color: ${Colours.LIME};
        }
    }
`;

CardCalculationModal.Separator = styled.div`
    grid-column: 1 / -1;
    margin: ${Sizes.SPACING.ONE} ${Sizes.SPACING.ONE_AND_A_HALF};

    // inconsistent render results observed when using the Marshall Technique at 0.0625rem
    border-top: 1px solid ${Colours.STONE};
`;

CardCalculationModal.BoxHeader = styled.div`
    grid-column: 1 / -1;

    border: 1px solid ${Colours.YUZU_LIGHT};
    background-color: ${rgba(Colours.YUZU, 0.3)};

    box-sizing: content-box;
    padding: ${Sizes.SPACING.HALF};
    margin-top: ${Sizes.SPACING.ONE};
`;


export default observer(CardCalculationModal);
